import { classNames } from 'tradera/utils/class-names';
import type React from 'react';
import { forwardRef } from 'react';
import type { PolyRefFunction, OnlyAs } from 'react-polymorphed';

import styles from './text.module.scss';

const polyRef = forwardRef as PolyRefFunction;

export type FontWeight = 'light' | 'medium' | 'heavy';

// This can probably be used by more components
export type FontColor =
    | 'branded'
    | 'danger'
    | 'dark'
    | 'function'
    | 'info'
    | 'light'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'white'
    | 'gray-100'
    | 'gray-200'
    | 'gray-300'
    | 'gray-400'
    | 'gray-500'
    | 'gray-600'
    | 'gray-700'
    | 'gray-800'
    | 'gray-900';

export type FontFamily = 'inter' | 'hansen';

export type TextProps = {
    size?:
        | 'london'
        | 'amsterdam'
        | 'paris'
        | 'dublin'
        | 'oslo'
        | 'madrid'
        | 'berlin';
    className?: string;
    align?: 'left' | 'center' | 'right';
    color?: FontColor;
    children?: React.ReactNode;
    fontWeight?: FontWeight;
    fontStyle?: 'italic';
    font?: FontFamily;
    nowrap?: boolean;
};

/**
 * Text component
 *
 * Defaults to paragraph, but can be used as "span", "strong", "code" or "em" also with `as="span"` etc.
 *
 * Font sizes (default is "paris"):
 * - london: 16px
 * - amsterdam: small screen: 14px, large screen: 16
 * - paris: small screen: 14px, large screen: 14px
 * - dublin: small screen: 12px, large screen: 14px
 * - oslo: small screen: 12px, large screen: 12px;
 *
 * @example
 * <Text>Some text</Text>
 *
 * @example
 * <Text as="span">Some text</Text>
 *
 * @example
 * <Text as="em" size="oslo">Some text</Text>
 */
export const Text = polyRef<
    'p',
    TextProps,
    OnlyAs<'p' | 'span' | 'strong' | 'em' | 'code' | 'b' | 'label'>
>(
    (
        {
            as: Component = 'p',
            size = 'paris',
            children,
            align,
            className,
            color,
            fontWeight,
            fontStyle,
            font,
            nowrap,
            ...props
        },
        ref
    ) => {
        return (
            <Component
                className={classNames(
                    styles.reset,
                    styles.wrapper,
                    {
                        [`size-${size}`]: size,
                        [`text-${align}`]: align,
                        [`text-${color}`]: color,
                        [`font-${fontWeight}`]: fontWeight,
                        ['font-italic']: fontStyle === 'italic',
                        [`font-${font}`]: font,
                        ['text-nowrap']: nowrap
                    },
                    className
                )}
                ref={ref}
                {...props}>
                {children}
            </Component>
        );
    }
);
